import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import { EditorAttachments } from "@/payroll/editor/editor-documents";
import { useAttachmentErrorToasts } from "@/payroll/editor/editor-documents/use-attachment-error-toasts";
import { useEditorUpload } from "@/payroll/editor/editor-documents/use-editor-upload";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { editorError } from "@/payroll/editor/state";
import type {
  DraftMessageAttachment,
  EnrichedTurnActivity,
  Workspace,
} from "@/payroll/editor/types";
import { EditorComposer } from "./editor-composer";
import { EditorTranscript } from "./editor-transcript";
import { useComposer } from "./use-composer";
import { useOutgoing } from "./use-outgoing";

type ConversationQueries = Pick<
  PayrollEditorQueries,
  | "messages"
  | "documents"
  | "send"
  | "upload"
  | "remove"
  | "refresh"
  | "open"
  | "cancel"
>;

export const EditorConversation = ({
  editor,
  workspace,
  businessId,
  workspaceId,
  storageKey,
}: {
  editor: ConversationQueries;
  workspace: Workspace;
  businessId: string;
  workspaceId: string;
  storageKey: string;
}) => {
  const composer = useComposer(storageKey);
  const active = editor.messages.data?.isActive ?? !!workspace.active_request;
  const requestId =
    editor.messages.data?.latest_request?.id ??
    workspace.active_request?.user_message_id;
  const filesProcessed = (attachments: DraftMessageAttachment[] = []) => {
    const ids = new Set(attachments.map((file) => file.client_upload_id));
    return (
      !upload.items.some((item) => ids.has(item.clientId)) &&
      (editor.documents.data?.every(
        (file) =>
          (file.submitted === false && !ids.has(file.client_upload_id)) ||
          file.status !== "pending",
      ) ??
        false)
    );
  };
  // Accept messages immediately. Only the HTTP request waits for its files.
  const canSend = workspace.editable;
  const canProcess =
    workspace.editable &&
    editor.documents.isSuccess &&
    !editor.remove.isPending;
  const outgoing = useOutgoing(
    `${storageKey}:outgoing`,
    editor.send,
    editor.messages.data,
    businessId,
    workspaceId,
    // The cancel endpoint stops whichever request is active when it arrives.
    // Do not start the next local request before that response returns.
    canProcess && !editor.cancel.isPending,
    (item) => filesProcessed(item.attachments),
  );
  const sentUploadIds = [
    ...outgoing.sentUploadIds,
    ...(editor.messages.data?.messages.flatMap((message) =>
      (message.attachments ?? []).flatMap((file) =>
        file.client_upload_id ? [file.client_upload_id] : [],
      ),
    ) ?? []),
  ];
  const upload = useEditorUpload(
    `${storageKey}:upload`,
    editor.upload,
    editor.documents,
    editor.remove,
    workspace.editable && !active && !editor.send.isPending,
    sentUploadIds,
  );
  useAttachmentErrorToasts(storageKey, editor.documents, upload.items);
  const sending = outgoing.sending;
  const preparing =
    workspace.editable && !active ? outgoing.preparing : undefined;
  const busy = active || sending || !!preparing || editor.cancel.isPending;
  const canStop =
    !!preparing || (active && !!requestId && !outgoing.failedToStart);
  const showActivity =
    active || !outgoing.posted.some((item) => item.failed && !item.saved);
  // A failed Stop belongs to the request it could not stop. Held by the
  // mutation alone it outlived that request, so an old failure could appear
  // beside a new one; and it only matters while there is still something to
  // stop.
  const stopFailed =
    busy && !!editor.cancel.error && editor.cancel.variables === requestId;
  const stopping =
    active &&
    (editor.cancel.isPending ||
      (editor.cancel.isSuccess && editor.cancel.variables === requestId));
  const discardOutgoing = (id: string) => {
    const item = outgoing.remove(id);
    if (item) upload.restoreDraft(item.attachments);
  };
  const stop = () => {
    if (preparing) discardOutgoing(preparing.id);
    else if (canStop && !stopping) editor.cancel.mutate(requestId);
  };
  const submit = () => {
    const message = composer.text.trim();
    if (
      !message ||
      !canSend ||
      composer.text.length > EDITOR_LIMITS.messageLength
    )
      return;
    composer.setText("");
    outgoing.add(message, !busy, upload.attachments);
    upload.clearRecent();
  };
  const sendReply = (text: string) => {
    if (!canSend || !text.trim()) return;
    outgoing.add(text, !busy, upload.attachments);
    upload.clearRecent();
  };
  const sendNow = (id: string) => {
    if (!canSend) return;
    outgoing.promote(id);
    stop();
  };

  const preparingIds = new Set(
    preparing?.attachments?.map((file) => file.client_upload_id),
  );
  const filesFailed = upload.items.some(
    (file) => preparingIds.has(file.clientId) && file.status === "failed",
  );
  const preparation: EnrichedTurnActivity | undefined = preparing
    ? {
        requestId: preparing.id,
        ended: false,
        running: !filesFailed && !editor.documents.isError,
        label:
          filesFailed || editor.documents.isError
            ? "Files need attention"
            : "Reading your files…",
        detail: editor.documents.isError
          ? "Refresh the file list to continue."
          : filesFailed
            ? "Open Sources to retry the upload, or cancel this message to remove the file."
            : undefined,
        steps: [],
      }
    : undefined;

  return (
    <section
      ref={composer.pane}
      // Focusable, but not in the tab order. A click on the transcript has
      // nothing of its own to focus, so without this the browser left focus
      // where it was and the pane never heard the keys meant for it.
      tabIndex={-1}
      className="flex min-h-0 min-w-0 flex-col bg-surface-raised outline-hidden [container:conversation/size]"
      aria-label="Payroll change conversation"
    >
      <EditorAttachments
        mode="context"
        sentUploadIds={sentUploadIds}
        pendingUploadIds={outgoing.sentUploadIds}
        documents={editor.documents}
        remove={editor.remove}
        upload={upload}
      />
      <EditorTranscript
        page={editor.messages.data}
        loading={editor.messages.isPending && !editor.messages.data}
        isSending={sending || !!preparing}
        onReply={sendReply}
        onWriteOwn={composer.focus}
        onResend={sendReply}
        canResend={canSend}
        canRetry={
          canProcess &&
          outgoing.posted.every((item) => filesProcessed(item.attachments))
        }
        posted={outgoing.posted}
        onRetry={outgoing.retry}
        onDiscard={discardOutgoing}
        queueCount={outgoing.queued.length}
        activity={
          preparation ?? (showActivity ? editor.messages.data?.activity : null)
        }
        active={active || !!preparing}
        sending={sending}
        stopping={stopping}
      />
      {/* A failed Stop only matters while there is something to stop. The
          request it could not stop finishes a moment later more often than
          not, and the sentence used to stay on screen afterwards, telling the
          owner to try again on a request that had already ended. Derived from
          `busy`, so it goes when the reason for it goes. */}
      {!!(editor.messages.error || stopFailed) && (
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          role="alert"
          className="flex flex-wrap items-center gap-3 px-5 py-2 text-xs"
        >
          <span>
            {stopFailed
              ? "Could not stop the request. Try again when the current request finishes."
              : editorError(editor.messages.error)}
          </span>
          {/* Refresh clears what it is offered for. Invalidating the queries
              left the mutation holding its old error, so the line it was meant
              to clear was still there afterwards. */}
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              editor.cancel.reset();
              editor.refresh();
            }}
          >
            Refresh
          </Button>
        </Stack>
      )}
      <EditorComposer
        composer={composer}
        documents={editor.documents}
        remove={editor.remove}
        upload={upload}
        editable={workspace.editable}
        disabled={!canSend}
        open={editor.open}
        busy={busy}
        canStop={canStop}
        stopping={stopping}
        onStop={stop}
        stopLabel={preparing ? "Cancel" : "Stop"}
        onSubmit={submit}
        queued={outgoing.queued}
        onQueueSendNow={sendNow}
        onQueueEdit={outgoing.amend}
        onQueueRemove={discardOutgoing}
      />
    </section>
  );
};
